import React, { useEffect, useRef } from "react";
import { alpha, Box, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import { RegistryVariable } from "../../registry/sitePageRegistry";
import { splitTemplate } from "../../utils/templateTokens";
import VariableChips from "./VariableChips";

const ZERO_WIDTH_SPACE = "​";

interface TemplateInputProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  variables?: RegistryVariable[];
  multiline?: boolean;
  helperText?: string;
  placeholder?: string;
}

// A merge-tag editor: {{tokens}} in `value` render as sample-text chips so
// marketing sees a realistic preview, but the raw {{token}} is always what
// gets read back out via onChange — chips are cosmetic, never the stored value.
const TemplateInput: React.FC<TemplateInputProps> = ({
  label,
  value,
  onChange,
  variables = [],
  multiline,
  helperText,
  placeholder,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastEmittedRef = useRef<string | null>(null);

  const findVariable = (key: string) => variables.find((variable) => variable.key === key);

  const createChipNode = (variable: RegistryVariable) => {
    const chip = document.createElement("span");
    chip.setAttribute("contenteditable", "false");
    chip.dataset.token = variable.key;
    chip.textContent = variable.sample;
    chip.style.cssText = [
      "display:inline-block",
      "padding:1px 8px",
      "margin:0 1px",
      "border-radius:12px",
      `background:${alpha(theme.palette.info.main, 0.14)}`,
      `color:${theme.palette.info.dark}`,
      "font-weight:600",
      `font-size:${multiline ? "0.85em" : "0.95em"}`,
      "white-space:nowrap",
    ].join(";");
    return chip;
  };

  const renderContent = (raw: string) => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";
    splitTemplate(raw).forEach((segment) => {
      if (segment.type === "text") {
        container.appendChild(document.createTextNode(segment.value));
        return;
      }
      const variable = findVariable(segment.key);
      if (variable) {
        container.appendChild(createChipNode(variable));
        container.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
      } else {
        container.appendChild(document.createTextNode(`{{${segment.key}}}`));
      }
    });
  };

  const extractRaw = (): string => {
    const container = containerRef.current;
    if (!container) return "";
    let raw = "";
    container.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        raw += (node.textContent || "").replaceAll(ZERO_WIDTH_SPACE, "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        raw += element.dataset.token ? `{{${element.dataset.token}}}` : element.textContent || "";
      }
    });
    return raw;
  };

  // Only rebuild the DOM when `value` changed from outside this component
  // (loading a different page) — never on our own onChange echo, or every
  // keystroke would reset the caret.
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      renderContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInput = () => {
    const raw = extractRaw();
    lastEmittedRef.current = raw;
    onChange(raw);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!multiline && event.key === "Enter") event.preventDefault();
  };

  const insertToken = (variable: RegistryVariable) => {
    const container = containerRef.current;
    if (!container) return;
    const selection = window.getSelection();
    let range: Range;
    if (selection && selection.rangeCount > 0 && container.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(container);
      range.collapse(false);
    }

    range.deleteContents();
    const chip = createChipNode(variable);
    range.insertNode(chip);

    const marker = document.createTextNode(ZERO_WIDTH_SPACE);
    const afterChip = document.createRange();
    afterChip.setStartAfter(chip);
    afterChip.collapse(true);
    afterChip.insertNode(marker);

    const caretRange = document.createRange();
    caretRange.setStartAfter(marker);
    caretRange.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(caretRange);
    container.focus();

    handleInput();
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.5, color: reduxTheme.primary_colors[400] }}
      >
        {label}
      </Typography>
      <Box
        ref={containerRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        sx={{
          minHeight: multiline ? 72 : 40,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "text.primary",
          bgcolor: "background.paper",
          border: `1px solid ${reduxTheme.primary_colors[400]}`,
          borderRadius: "10px",
          px: 1.75,
          py: 1,
          fontSize: "0.95rem",
          lineHeight: 1.5,
          outline: "none",
          "&:focus": {
            borderColor: "#614438",
            boxShadow: "0 0 0 1px #614438",
          },
          "&:empty:before": {
            content: "attr(data-placeholder)",
            color: "text.disabled",
          },
        }}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
      {variables.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <VariableChips variables={variables} onInsert={insertToken} />
        </Box>
      )}
    </Box>
  );
};

export default TemplateInput;
